/**
 * --------------------------------------------------------------------------
 * Liander Bootstrap livechat.js
 * --------------------------------------------------------------------------
 */

import {
  defineJQueryPlugin
} from '../../node_modules/bootstrap/js/src/util/index.js'
import EventHandler from '../../node_modules/bootstrap/js/src/dom/event-handler.js'
import SelectorEngine from '../../node_modules/bootstrap/js/src/dom/selector-engine.js'
import BaseComponent from '../../node_modules/bootstrap/js/src/base-component.js'

/**
 * Constants
 */

const NAME = 'livechat'
const DATA_KEY = 'lbs.livechat'
const EVENT_KEY = `.${DATA_KEY}`
const DATA_API_KEY = '.data-api'

const ACTIVE_CHAT_COOKIE = 'activeChatChannel'
const COOKIE_EXPIRES_IN_DAYS = 1

const TRIGGER_NAME_INIT = 'livechat-init'
const TRIGGER_NAME_OPEN = 'livechat-open'
const TRIGGER_NAME_START_BUTTON = 'livechat-start-button'
const TRIGGER_NAME_TRIGGER = 'livechat-trigger'

const EVENT_SHOWN = `shown${EVENT_KEY}`
const EVENT_CLOSED = `closed${EVENT_KEY}`
const EVENT_CLICK_DATA_API = `click${EVENT_KEY}${DATA_API_KEY}`

const CLASS_NAME_SHOW = 'in'

const SELECTOR_DATA_DISMISS = '[data-bs-dismiss="livechat"]'
const SELECTOR_DATA_TOGGLE = '[data-bs-toggle="livechat"]'
const SELECTOR_LIVECHAT = '.livechat'
const SELECTOR_START_BUTTON = '.livechat-start-button'

const Default = {
  startButtonEnabled: true
}

const DefaultType = {
  startButtonEnabled: 'boolean'
}

/**
 * Class definition
 */

class Livechat extends BaseComponent {
  constructor(element, config) {
    super(element, config)

    this._init()
  }

  // Getters
  static get Default() {
    return Default
  }

  static get DefaultType() {
    return DefaultType
  }

  static get NAME() {
    return NAME
  }

  // Public

  open(trigger = TRIGGER_NAME_OPEN) {
    if (Livechat.activeChatExists()) {
      this._openChat(Livechat.getCookie(ACTIVE_CHAT_COOKIE), trigger)
    } else {
      this._openChat(this._config.chatChannel, trigger)
    }
  }

  close() {
    this._closeChat()
  }

  // Private
  _init() {
    if (!this._config.chatChannel) {
      this._config.startButtonEnabled = false
    }

    if (Livechat.activeChatExists()) {
      this._openChat(Livechat.getCookie(ACTIVE_CHAT_COOKIE), TRIGGER_NAME_INIT)
    } else {
      this._showStartButton()
    }
  }

  _getStartButton() {
    return SelectorEngine.findOne(SELECTOR_START_BUTTON)
  }

  _openChat(chatChannelUrl, trigger) {
    const isOpen = this._element.classList.contains(CLASS_NAME_SHOW)

    if (!isOpen) {
      this._hideStartButton()
      const iframe = SelectorEngine.findOne('iframe', this._element)
      iframe.setAttribute('src', chatChannelUrl)
      this._element.classList.add(CLASS_NAME_SHOW)
      EventHandler.trigger(this._element, EVENT_SHOWN, {
        trigger
      })
      this._setActiveChatCookie()
    }
  }

  _closeChat() {
    this._element.classList.remove(CLASS_NAME_SHOW)
    EventHandler.trigger(this._element, EVENT_CLOSED)
    this._removeActiveChatCookie()
    this._showStartButton()
  }

  _hideStartButton() {
    this._getStartButton().classList.remove(CLASS_NAME_SHOW)
  }

  _showStartButton() {
    if (this._config.startButtonEnabled) {
      this._getStartButton().classList.add(CLASS_NAME_SHOW)
    }
  }

  _setActiveChatCookie() {
    const cookieOptions = {}
    if (this._config.cookieDomain) {
      cookieOptions.domain = this._config.cookieDomain
    }

    Livechat.setCookie(ACTIVE_CHAT_COOKIE, this._config.chatChannel, cookieOptions)
  }

  _removeActiveChatCookie() {
    const cookieOptions = {
      'max-age': -1
    }
    if (this._config.cookieDomain) {
      cookieOptions.domain = this._config.cookieDomain
    }

    Livechat.setCookie(ACTIVE_CHAT_COOKIE, '', cookieOptions)
  }

  // Static
  static activeChatExists() {
    return Livechat.getCookie(ACTIVE_CHAT_COOKIE)
  }

  static getCookie(cookieName) {
    const name = `${cookieName}=`
    const decodedCookie = decodeURIComponent(document.cookie)
    const cookies = decodedCookie.split(';')

    for (const cookie of cookies) {
      let currCookie = cookie
      while (currCookie.charAt(0) === ' ') {
        currCookie = currCookie.slice(1)
      }

      if (currCookie.indexOf(name) === 0) {
        return currCookie.slice(name.length)
      }
    }

    return null
  }

  static setCookie(cookieName, cookieValue, options) {
    const hours = 24
    const minutes = 60
    const seconds = 60
    const milliseconds = 1000
    const expires = new Date()
    expires.setTime(expires.getTime() + (COOKIE_EXPIRES_IN_DAYS * hours * minutes * seconds * milliseconds))

    const defaultOptions = {
      path: '/',
      expires: expires.toUTCString(),
      samesite: 'lax'
    }

    options = { ...defaultOptions, ...options }

    let updatedCookie = `${encodeURIComponent(cookieName)}=${encodeURIComponent(cookieValue)}`

    for (const optionKey in options) {
      if (Object.prototype.hasOwnProperty.call(options, optionKey)) {
        updatedCookie = `${updatedCookie}; ${optionKey}`
        const optionValue = options[optionKey]
        if (optionValue !== true) {
          updatedCookie = `${updatedCookie}=${optionValue}`
        }
      }
    }

    // eslint-disable-next-line unicorn/no-document-cookie
    document.cookie = updatedCookie
  }

  static jQueryInterface(config) {
    return this.each(function () {
      const data = Livechat.getOrCreateInstance(this, config)

      if (typeof config === 'string') {
        if (typeof data[config] === 'undefined') {
          throw new TypeError(`No method named "${config}"`)
        }

        data[config]()
      }
    })
  }
}

/**
 * Data API implementation
 */

EventHandler.on(document, EVENT_CLICK_DATA_API, SELECTOR_START_BUTTON, () => {
  const element = SelectorEngine.findOne(SELECTOR_LIVECHAT)
  Livechat.getOrCreateInstance(element).open(TRIGGER_NAME_START_BUTTON)
})

EventHandler.on(document, EVENT_CLICK_DATA_API, SELECTOR_DATA_TOGGLE, () => {
  const element = SelectorEngine.findOne(SELECTOR_LIVECHAT)
  Livechat.getOrCreateInstance(element).open(TRIGGER_NAME_TRIGGER)
})

EventHandler.on(document, EVENT_CLICK_DATA_API, SELECTOR_DATA_DISMISS, () => {
  const element = SelectorEngine.findOne(SELECTOR_LIVECHAT)
  Livechat.getOrCreateInstance(element).close()
})

/**
 * jQuery
 */

defineJQueryPlugin(Livechat)

export default Livechat
